import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { filterObject } from "@/utils/helpers";
import { useSystem } from "@/utils/hooks/useLocation";

// ******* Queries *******

export const useGetMakkahGroupsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  // Build query parameters
  const queryParams = new URLSearchParams();

  // Add search parameter if provided
  if (params.query && params.query.trim()) {
    queryParams.append("query", params.query.trim());
  }

  // Add page parameter if provided
  if (params.page) {
    queryParams.append("page", params.page);
  }

  // Add other parameters if needed
  Object.keys(params).forEach((key) => {
    if (
      key !== "query" &&
      key !== "page" &&
      params[key] !== undefined &&
      params[key] !== null &&
      params[key] !== ""
    ) {
      queryParams.append(key, params[key]);
    }
  });

  const queryString = queryParams.toString();
  const url = queryString
    ? `${currentSystem}/makkah-groups?${queryString}`
    : `${currentSystem}/makkah-groups`;

  return useQuery({
    queryKey: ["makkah-groups", params, currentSystem],
    queryFn: () => axiosProtected.get(url),
  });
};

/** GET {system}/departure-group — paginated departure groups (groups → Departure tab) */
export const useGetMakkahDepartureGroupsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const filtered = filterObject(params);
  const queryParams = new URLSearchParams();
  Object.keys(filtered).forEach((key) => {
    queryParams.append(key, filtered[key]);
  });
  const queryString = queryParams.toString();
  const url = queryString
    ? `${currentSystem}/departure-group?${queryString}`
    : `${currentSystem}/departure-group`;

  return useQuery({
    queryKey: ["makkah-departure-group", filtered, currentSystem],
    queryFn: () => axiosProtected.get(url),
  });
};

export const useGetOneDepartureGroupQuery = ({ id }) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  return useQuery({
    queryKey: ["one-departure-group", id, currentSystem],
    queryFn: () => axiosProtected.get(`${currentSystem}/departure-group/${id}`),
    enabled: !!id,
  });
};

export const useCreateDepartureGroupSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: (payload) =>
      axiosProtected.post(`${currentSystem}/departure-group`, payload),
    onSuccess: () => {
      toast.success(t("DEPARTURE_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure-group"] });
    },
  });
};

export const useUpdateDepartureGroupSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      axiosProtected.post(`${currentSystem}/departure-group/${id}`, payload),
    onSuccess: () => {
      toast.success(t("DEPARTURE_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure-group"] });
    },
  });
};

export const useDeleteDepartureGroupSegmentMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: ({ id }) =>
      axiosProtected.delete(`${currentSystem}/departure-group/delete/${id}`),
    onSuccess: () => {
      toast.success(t("DELETE_DEPARTURE_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["one-departure-group"] });
    },
  });
};

export const useGetRawGroupNamesQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const { mission_id } = params;

  return useQuery({
    queryKey: ["raw-group-names", params],
    queryFn: () => axiosProtected.get(`${currentSystem}/pilgrim-group-names`, {
      params
    }),
    enabled: !!mission_id,
  });
};

/**
 * GET makkah/inquiries/group-pilgrims - paginated list with filters
 */
export const useGetGroupPilgrimsInquiryQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const filteredParams = filterObject(params);
  return useQuery({
    queryKey: ["group-pilgrims-inquiry", filteredParams],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/inquiries/group-pilgrims`, {
        params: filteredParams,
      }),
  });
};

export const useGetHouseContractsQuery = ({ enabled = false } = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["house-contracts"],
    queryFn: () => axiosProtected.get(`${currentSystem}/house-contracts`),
    enabled,
  });
};

// ******* Mutations *******

export const useDeleteMakkahGroupMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: ({ id }) => {
      return axiosProtected.get(`${currentSystem}/makkah-groups/delete/${id}`);
    },
    onSuccess: () => {
      toast.success(t("GROUP_ITEM_DELETED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
    },
    onError: (err) => {
      console.error("Group delete error:", err);
      toast.error(
        err?.response?.data?.message || t("GROUP_ITEM_DELETE_FAILED")
      );
    },
  });
};

export const useImportMakkahDepartureGroupMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      return axiosProtected.post(`${currentSystem}/departure-group/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      toast.success(t("GROUP_IMPORTED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["makkah-departure-group"] });
    },
    onError: (err) => {
      console.error("Departure group import error:", err);
      toast.error(err?.response?.data?.message || t("GROUP_IMPORT_FAILED"));
    },
  });
};

export const useImportMakkahGroupMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      return axiosProtected.post(`${currentSystem}/groups-dummy/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      toast.success(t("GROUP_IMPORTED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
    },
    onError: (err) => {
      console.error("Group import error:", err);
      toast.error(err?.response?.data?.message || t("GROUP_IMPORT_FAILED"));
    },
  });
};
